import { Receipt } from 'lucide-react';
import Card from '../ui/Card';

/**
 * Payment History — shared invoice table.
 *
 * Used in:
 *   - OrganizationDetailPage (admin) → Billing tab
 *   - UserDetailPage (admin)         → Billing & Plan tab (for solo trainers)
 *
 * Backend supplies a `paymentHistory` array on org/user responses with this
 * shape (see organizationController.getOrganization & userController.getUser):
 *   { id, invoiceNumber, description, plan, amount, subtotal, tax, taxRate,
 *     status, date, externalId }
 */

const STATUS_STYLES = {
  paid:      { label: 'Paid',      class: 'bg-green-50 text-green-700 border-green-200' },
  pending:   { label: 'Pending',   class: 'bg-amber-50 text-amber-700 border-amber-200' },
  failed:    { label: 'Failed',    class: 'bg-red-50 text-red-700 border-red-200' },
  refunded:  { label: 'Refunded',  class: 'bg-purple-50 text-purple-700 border-purple-200' },
  cancelled: { label: 'Cancelled', class: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  draft:     { label: 'Draft',     class: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
};

const formatINR = (amount) =>
  `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const PaymentHistoryCard = ({ paymentHistory = [] }) => {
  const rows = Array.isArray(paymentHistory) ? paymentHistory : [];

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Receipt size={18} className="text-neutral-500" />
          <h3 className="text-lg font-semibold text-secondary-900">Payment History</h3>
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-3">
              <Receipt size={22} className="text-neutral-400" />
            </div>
            <p className="text-sm font-medium text-neutral-700">No invoices yet</p>
            <p className="text-xs text-neutral-500 mt-1">
              Payments will appear here once a subscription has been billed.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider">Invoice #</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider">Description</th>
                    <th className="text-right py-3 px-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider">Amount</th>
                    <th className="text-left py-3 px-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const status = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
                    return (
                      <tr key={row.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/50">
                        <td className="py-3 px-3 text-sm text-neutral-700 whitespace-nowrap">{formatDate(row.date)}</td>
                        <td className="py-3 px-3 text-sm font-mono text-neutral-900">{row.invoiceNumber || '—'}</td>
                        <td className="py-3 px-3 text-sm text-neutral-700">
                          {row.description || (row.plan ? `${row.plan.replace('_', ' ')} plan` : '—')}
                        </td>
                        <td className="py-3 px-3 text-sm font-semibold text-neutral-900 text-right whitespace-nowrap">
                          {formatINR(row.amount)}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${status.class}`}>
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-neutral-400 mt-4 text-right">
              Showing the {rows.length} most recent {rows.length === 1 ? 'invoice' : 'invoices'}.
            </p>
          </>
        )}
      </div>
    </Card>
  );
};

export default PaymentHistoryCard;
